import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Hook para recarregar dados quando há mudanças de rota/navegação
 * 
 * Diferente de visibilitychange/focus, este hook reage apenas a:
 * - Mudanças de pathname (/obras → /diario)
 * - Mudanças de query params (?id=1 → ?id=2)
 * - Mudanças de hash (#section1 → #section2)
 * - Tipo de navegação (POP = back/forward, PUSH = nova rota, REPLACE = substituir)
 * 
 * @param onRefresh Função para recarregar dados (pode ser síncrona ou assíncrona)
 * 
 * @example
 * ```typescript
 * // Em uma página de listagem
 * useRouteRefresh(carregarObras);
 * 
 * // Em uma página de detalhes
 * useRouteRefresh(() => {
 *   if (obraId) carregarObraDetalhes(obraId);
 * });
 * ```
 */
export function useRouteRefresh(onRefresh: () => void | Promise<void>) {
  const location = useLocation();
  const navType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
  
  useEffect(() => {
    onRefresh();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [location.pathname, location.search, location.hash, navType]);

  // 🔹 NOVO: também reage a "gatilhos manuais" via window.dispatchEvent
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ scope?: string }>;
      const scope = ce.detail?.scope;
      // se não houver escopo, sempre atualiza;
      // se houver escopo, atualiza apenas se o pathname bater com o prefixo
      if (!scope || location.pathname.startsWith(scope)) {
        void onRefresh();
      }
    };
    window.addEventListener('app:refresh', handler as EventListener);
    return () => window.removeEventListener('app:refresh', handler as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
}

/**
 * 🔸 Helper opcional para acionar refresh manualmente (ex.: após Salvar)
 * @param scope Prefixo de rota (ex.: "/compras", "/obras/123"), ou deixe vazio para refresh global
 */
export function refreshNow(scope?: string) {
  window.dispatchEvent(new CustomEvent('app:refresh', { detail: { scope } }));
}
